import { Box, CircularProgress } from '@mui/material';
import { ButtonWrapper } from '../../../../../../shared/components/formUtilities/useButton';
import { ArrowIcons } from '../../../../../../shared/components/common/arrowIcons';
import { useEffect, useState } from 'react';
import { TextWrapper } from '../../../../../../shared/components/formUtilities/useTextWrapper';
import { useTranslation } from 'react-i18next';
import { InputWrapper } from '../../../../../../shared/components/formUtilities/useInput';
import { FormikProvider, useFormik } from 'formik';
import { FormHelper } from '../../../../../../helpers/formHelper';
import { InvestorBusinessKSASchema } from '../../../../../../shared/validations/schema';
import { InvestorRegService } from '../../../investorRegService';
import {
	getRegistrationData,
	saveTokenFromRegistration,
	setRegistrationData,
	updateInvestorSteps,
} from '../../../../../../services/authService';
import { Toaster } from '../../../../../../shared/components/common/toaster';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { InvestorDeclarationDialog } from '../../declarationDialog';

export const BusinessInformationKSA = ({ onGoBackKSA, isLoading = false }) => {
	const investorRegService = new InvestorRegService();
	const [loading, setLoading] = useState(isLoading);
	const [loadingSave, setLoadingSave] = useState(isLoading);
	const [selectedCompanyId, setSelectedCompanyId] = useState(null);
	const [companies, setCompanies] = useState([]);
	const [showCompanies, setShowCompanies] = useState(true);
	const [showDeclartionPopup, setDeclartionPopup] = useState(false);
	const { t } = useTranslation();

	const form = useFormik({
		initialValues: { tradeLicenseNumber: '' },
		validationSchema: InvestorBusinessKSASchema,
		onSubmit: () => {
			onSubmitBusinessInfo();
		},
		...FormHelper.formikProps(),
	});

	useEffect(() => {
		setLoading(true);
		setTimeout(() => {
			getAllData();
		}, 500);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getAllData = async () => {
		try {
			setLoading(true);
			const [companyDetails, businessInformation] = await Promise.all([
				investorRegService.requestWathq(),
				investorRegService.getInvestorProfileBusinessInfo(),
			]);
			let companyData = companyDetails.data;
			setCompanies(companyData || []);
			let businessData = businessInformation.data;
			if (businessData?.tradeLicenseNumber) {
				let isIdAvailable = companyData?.some(
					(resp) =>
						resp?.commercialRegistrationNumber ===
						businessData?.tradeLicenseNumber
				);
				if (isIdAvailable) {
					setSelectedCompanyId(businessData?.tradeLicenseNumber);
				} else {
					form.setFieldValue(
						'tradeLicenseNumber',
						businessData?.tradeLicenseNumber
					);
					setShowCompanies(false);
				}
			}
			setLoading(false);
		} catch {
			setLoading(false);
		}
	};

	const onSubmitBusinessInfo = () => {
		let crNumber =
			showCompanies && companies.length
				? selectedCompanyId
				: form.values.tradeLicenseNumber;
		if (!crNumber) {
			Toaster({ msg: "Cant't find Commercial Registration Number" });
			return;
		}
		setLoadingSave(true);
		investorRegService
			.registerCRNumber(crNumber)
			.then((resp) => {
				afterRegisterCRNumber(resp.data);
				setLoadingSave(false);
			})
			.catch((err) => {
				setLoadingSave(false);
			});
	};

	const afterRegisterCRNumber = (resp) => {
		let registrationModel = resp?.registrationModel;
		saveTokenFromRegistration(resp?.loginModel?.token);
		let regData = {
			...getRegistrationData(),
			step: registrationModel.step,
			investorType:
				registrationModel?.personalInformation?.investorType?.name,
			requestedStep: 1,
		};
		setRegistrationData(regData);
		updateInvestorSteps(registrationModel.step);
		redirectToNextStep();
	};

	const redirectToNextStep = () => {
		setDeclartionPopup(true);
	};

	return <Box>
		{!loading ? (
			<Box className="flex flex-col justify-center items-center">
				<Box className="md:w-[465px] w-full flex flex-col company-wrapper rounded-md">
					{showCompanies && companies.length ? (
						<Box className="flex flex-col">
							<Box className="flex flex-col p-6 pb-3">
								<TextWrapper
									classList="heading !text-[14px] !text-center"
									text={t('companiesWithAssociatedId')}
								/>
								<TextWrapper
									classList="!caption !text-[13px] !text-center"
									text={t('pleaseSelectAnyCompany')}
								/>
							</Box>
							<Box className="company-grid px-6 pt-4">
								<Box className="grid grid-cols-1 gap-2 ">
									{companies.map((resp, index) => {
										return (
											<Box
												data-testid="companyItem"
												onClick={() => {
													setSelectedCompanyId(
														resp?.commercialRegistrationNumber
													);
												}}
												key={index}
												className={`bg-white relative company-card rounded-md p-3 flex-col justify-center flex ${selectedCompanyId === resp?.commercialRegistrationNumber
													? 'active-company'
													: ''
													}`}>
												<CheckCircleIcon
													className={`header-active-icon ${selectedCompanyId ===
														resp?.commercialRegistrationNumber
														? 'opacity-100'
														: 'opacity-0'
														}`}
												/>
												<Box
													data-testid={resp.name}
													title={resp.name}
													className="heading !text-[14px] !text-start">
													{resp.name}
													<span className="!text-[14px] !text-start">
														&nbsp;({resp.commercialRegistrationNumber})&nbsp;
													</span>
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
							<Box className="p-7 flex justify-center items-center">
								<ButtonWrapper
									dataTestID="backBtn"
									classList="!secondary-btn !mx-2"
									disabled={loadingSave}
									largeBtn
									icon={<ArrowIcons isInvert></ArrowIcons>}
									label={t('back')}
									onClick={() => {
										onGoBackKSA && onGoBackKSA();
									}}
								/>
								<ButtonWrapper
									dataTestID="nextBtnCompany"
									disabled={!selectedCompanyId}
									loading={loadingSave}
									largeBtn
									label={t('next')}
									onClick={() => {
										onSubmitBusinessInfo();
									}}
								/>
							</Box>
						</Box>
					) : (
						<Box className="flex flex-col">
							<Box className="flex flex-col p-6 pb-3">
								<TextWrapper
									classList="heading !text-[14px] !text-center"
									text={t('companiesNotAssociatedId')}
								/>
								<TextWrapper
									classList="!caption !text-[13px] !text-center"
									text={t('companiesNotAssociatedIdInfo')}
								/>
							</Box>
							<Box className="px-6 pt-4 pb-7">
								<FormikProvider value={form}>
									<InputWrapper
										dataTestID="tradeLicenseNumber"
										errorStateTestID="tradeLicenseNumberError"
										key={'tradeLicenseNumber'}
										name={'tradeLicenseNumber'}
										label={t('crNumber')}
									/>
									<Box className="flex items-center justify-center mt-7">
										<ButtonWrapper
											dataTestID="backBtn"
											classList="!secondary-btn md:!mx-0 !mx-2"
											disabled={loadingSave}
											largeBtn
											icon={<ArrowIcons isInvert></ArrowIcons>}
											label={t('back')}
											onClick={() => {
												onGoBackKSA && onGoBackKSA();
											}}
										/>
										<ButtonWrapper
											dataTestID="nextBtn"
											classList="md:!mx-3 !mx-1"
											loading={loadingSave}
											largeBtn
											endIcon={<ArrowIcons></ArrowIcons>}
											label={t('next')}
											onClick={() => {
												form.submitForm();
											}}
										/>
									</Box>
								</FormikProvider>
							</Box>
						</Box>
					)}
				</Box>
				{companies.length ? (
					<Box className="flex items-center justify-center mt-6">
						<Box
							className="underline-link text-primary !text-center font-[600]"
							onClick={() => {
								setSelectedCompanyId(null);
								form.setFieldValue('tradeLicenseNumber', '');
								form.setFieldTouched('tradeLicenseNumber', false);
								setShowCompanies(!showCompanies);
							}}>
							{!showCompanies
								? t('DoYouWantAnotherCRnumber')
								: t('DoYouWantRegisterCRnumber')}
						</Box>
					</Box>
				) : null}


			</Box>
		) : (
			<Box className="flex justify-center items-center reg-section-height">
				<CircularProgress />
			</Box>
		)}
		{showDeclartionPopup ? (
			<InvestorDeclarationDialog
				state={showDeclartionPopup}
				onClose={(e) => {
					setDeclartionPopup(false);
				}}></InvestorDeclarationDialog>
		) : null}
	</Box>;
};
